import { Fragment, useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const CROP_TYPES = {
  BACKGROUND: "background",
  LOGO: "logo",
  STAMP: "stamp",
  MILESTONE: "milestone",
};

const ASPECT_RATIOS = {
  [CROP_TYPES.BACKGROUND]: 1125 / 432,
  [CROP_TYPES.LOGO]: 1,
  [CROP_TYPES.STAMP]: 1,
  [CROP_TYPES.MILESTONE]: 1,
};

export default function ImageCropModal({
  isOpen,
  onClose,
  imageUrl,
  onCropComplete,
  type = CROP_TYPES.BACKGROUND,
  title,
  saveLabel = "Save",
  cancelLabel = "Cancel",
}) {
  const [crop, setCrop] = useState();
  const [imageRef, setImageRef] = useState(null);

  const onImageLoad = useCallback(
    (e) => {
      if (!e?.target?.width) {
        return;
      }
      const { width, height } = e.target;
      setImageRef(e.target);

      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: "%",
            width: 100,
          },
          ASPECT_RATIOS[type],
          width,
          height,
        ),
        width,
        height,
      );
      setCrop(crop);
    },
    [type],
  );

  const getCroppedImg = useCallback(
    async (image, crop, targetWidth = 1140, targetHeight = 435) => {
      const canvas = document.createElement("canvas");
      const pixelRatio = window.devicePixelRatio;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const pixelCrop = {
        x: (crop.x * image.width) / 100,
        y: (crop.y * image.height) / 100,
        width: (crop.width * image.width) / 100,
        height: (crop.height * image.height) / 100,
      };

      canvas.width = targetWidth * pixelRatio;
      canvas.height = targetHeight * pixelRatio;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No 2d context");

      ctx.scale(pixelRatio, pixelRatio);
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        targetWidth,
        targetHeight,
      );

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error("Canvas is empty");
              return;
            }
            resolve(URL.createObjectURL(blob));
          },
          "image/png",
          1,
        );
      });
    },
    [],
  );

  const handleCropComplete = useCallback(async () => {
    if (imageRef && crop?.width && crop?.height) {
      try {
        let targetWidth, targetHeight;
        switch (type) {
          case CROP_TYPES.BACKGROUND:
            targetWidth = 1140;
            targetHeight = 435;
            break;
          case CROP_TYPES.LOGO:
          case CROP_TYPES.STAMP:
          case CROP_TYPES.MILESTONE:
            targetWidth = 200;
            targetHeight = 200;
            break;
          default:
            targetWidth = 1140;
            targetHeight = 435;
        }

        const croppedImageUrl = await getCroppedImg(
          imageRef,
          crop,
          targetWidth,
          targetHeight,
        );
        if (croppedImageUrl) {
          onCropComplete(croppedImageUrl);
          onClose();
        }
      } catch (error) {
        console.error("Cropping failed:", error);
      }
    }
  }, [imageRef, crop, type, getCroppedImg, onCropComplete, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 mb-4"
                >
                  {title}
                </Dialog.Title>

                <div className="mt-2">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    aspect={ASPECT_RATIOS[type]}
                    minWidth={100}
                    circularCrop={
                      type === CROP_TYPES.STAMP || type === CROP_TYPES.MILESTONE
                    }
                  >
                    <img
                      src={imageUrl}
                      onLoad={onImageLoad}
                      alt="Crop preview"
                      style={{ maxWidth: "100%" }}
                      className={
                        type === CROP_TYPES.STAMP ||
                        type === CROP_TYPES.MILESTONE
                          ? "rounded-none"
                          : undefined
                      }
                    />
                  </ReactCrop>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-200"
                    onClick={handleCropComplete}
                  >
                    {saveLabel}
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={onClose}
                  >
                    {cancelLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// Export constants for external use
export { CROP_TYPES };
